// ============================
// GOOGLE SHEETS INTEGRATION
// ============================

const SCOPES = "https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive.file";
const DISCOVERY_DOC = "https://sheets.googleapis.com/$discovery/rest?version=v4";

let gapiLoaded = false;
let gisLoaded = false;
let tokenClient = null;

// Load Google API script
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src="' + src + '"]')) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export async function initGoogleAPI(clientId) {
  // Load gapi
  await loadScript("https://apis.google.com/js/api.js");
  await new Promise((resolve) => {
    window.gapi.load("client", resolve);
  });
  await window.gapi.client.init({});
  await window.gapi.client.load(DISCOVERY_DOC);
  gapiLoaded = true;

  // Load GIS (Google Identity Services)
  await loadScript("https://accounts.google.com/gsi/client");
  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: clientId,
    scope: SCOPES,
    callback: "", // set later
  });
  gisLoaded = true;

  return true;
}

export function isReady() {
  return gapiLoaded && gisLoaded;
}

export function getAccessToken() {
  return window.gapi && window.gapi.client && window.gapi.client.getToken();
}

export function signIn() {
  return new Promise((resolve, reject) => {
    if (!tokenClient) { reject(new Error("Google API not initialized")); return; }
    tokenClient.callback = (resp) => {
      if (resp.error) reject(resp);
      else resolve(resp);
    };
    const token = getAccessToken();
    if (token === null) {
      tokenClient.requestAccessToken({ prompt: "consent" });
    } else {
      tokenClient.requestAccessToken({ prompt: "" });
    }
  });
}

export function signOut() {
  const token = getAccessToken();
  if (token) {
    window.google.accounts.oauth2.revoke(token.access_token);
    window.gapi.client.setToken(null);
  }
}

export async function createSpreadsheet(title) {
  const resp = await window.gapi.client.sheets.spreadsheets.create({
    resource: {
      properties: { title: title },
      sheets: [
        { properties: { title: "Expenses" } },
        { properties: { title: "Team" } },
        { properties: { title: "Crypto Holdings" } },
        { properties: { title: "Loans" } },
        { properties: { title: "Payments" } },
        { properties: { title: "Summary" } },
      ],
    },
  });
  return resp.result;
}

export async function writeToSheet(spreadsheetId, sheetName, data) {
  await window.gapi.client.sheets.spreadsheets.values.update({
    spreadsheetId: spreadsheetId,
    range: sheetName + "!A1",
    valueInputOption: "RAW",
    resource: { values: data },
  });
}

export async function clearSheet(spreadsheetId, sheetName) {
  try {
    await window.gapi.client.sheets.spreadsheets.values.clear({
      spreadsheetId: spreadsheetId,
      range: sheetName + "!A:Z",
    });
  } catch (e) { /* sheet might not exist */ }
}

export async function readFromSheet(spreadsheetId, sheetName) {
  try {
    const resp = await window.gapi.client.sheets.spreadsheets.values.get({
      spreadsheetId: spreadsheetId,
      range: sheetName + "!A:Z",
    });
    return resp.result.values || [];
  } catch (e) { return []; }
}

export async function syncToGoogleSheets(spreadsheetId, data) {
  const ts = new Date().toLocaleString("en-IN");

  // Expenses
  await clearSheet(spreadsheetId, "Expenses");
  const expRows = [["Date", "Category", "Description", "Amount (INR)", "Payment Method"]];
  (data.expenses || []).forEach(e => expRows.push([e.date, e.category, e.description || "", Number(e.amount), e.paymentMethod || ""]));
  await writeToSheet(spreadsheetId, "Expenses", expRows);

  // Team
  await clearSheet(spreadsheetId, "Team");
  const teamRows = [["Name", "Role", "Salary (INR)", "Status", "Cycle", "Email", "Phone", "Join Date"]];
  (data.team || []).forEach(m => teamRows.push([m.name, m.role, Number(m.salary), m.status, m.salaryCycle || "Monthly", m.email || "", m.phone || "", m.joinedDate || ""]));
  await writeToSheet(spreadsheetId, "Team", teamRows);

  // Crypto Holdings
  await clearSheet(spreadsheetId, "Crypto Holdings");
  const cryptoRows = [["Symbol", "Name", "Amount", "Price at Receipt (INR)", "Source", "Date Received"]];
  (data.cryptoHoldings || []).forEach(h => cryptoRows.push([h.symbol, h.name, Number(h.amount), Number(h.priceAtReceipt) || 0, h.source, h.dateReceived || ""]));
  await writeToSheet(spreadsheetId, "Crypto Holdings", cryptoRows);

  // Loans
  await clearSheet(spreadsheetId, "Loans");
  const loanRows = [["Employee", "Amount (INR)", "EMI (INR)", "Reason", "Status", "Date"]];
  (data.loanRequests || []).forEach(l => loanRows.push([l.employee, Number(l.amount), Number(l.emi), l.reason || "", l.status, l.createdAt || ""]));
  await writeToSheet(spreadsheetId, "Loans", loanRows);

  // Payments
  await clearSheet(spreadsheetId, "Payments");
  const payRows = [["Employee", "Amount (INR)", "Date", "Type"]];
  (data.payments || []).forEach(p => payRows.push([p.employee || p.name || "", Number(p.amount), p.date || "", p.type || "Salary"]));
  await writeToSheet(spreadsheetId, "Payments", payRows);

  // Summary
  await clearSheet(spreadsheetId, "Summary");
  const totalExp = (data.expenses || []).reduce((s, e) => s + Number(e.amount), 0);
  const totalSal = (data.team || []).filter(m => m.status === "Active").reduce((s, m) => s + Number(m.salary), 0);
  const totalHoldings = (data.cryptoHoldings || []).reduce((s, h) => s + (Number(h.amount) * (Number(h.priceAtReceipt) || 0)), 0);
  await writeToSheet(spreadsheetId, "Summary", [
    ["FinBoard v11.3 — Backup Summary"],
    ["Last Synced", ts],
    [""],
    ["Metric", "Value"],
    ["Total Expenses", totalExp],
    ["Total Holdings (at receipt)", totalHoldings],
    ["Monthly Payroll", totalSal],
    ["Team Members", (data.team || []).length],
    ["Crypto Assets", (data.cryptoHoldings || []).length],
    ["Loan Requests", (data.loanRequests || []).length],
    ["Audit Events", (data.auditLog || []).length],
  ]);

  return ts;
}

export async function restoreFromGoogleSheets(spreadsheetId) {
  const expData = await readFromSheet(spreadsheetId, "Expenses");
  const teamData = await readFromSheet(spreadsheetId, "Team");
  const cryptoData = await readFromSheet(spreadsheetId, "Crypto Holdings");
  const loanData = await readFromSheet(spreadsheetId, "Loans");
  const payData = await readFromSheet(spreadsheetId, "Payments");

  const expenses = expData.slice(1).map((r, i) => ({
    id: "exp_r_" + i, date: r[0] || "", category: r[1] || "", description: r[2] || "",
    amount: Number(r[3]) || 0, paymentMethod: r[4] || "Cash",
  }));

  const team = teamData.slice(1).map((r, i) => ({
    id: "tm_r_" + i, name: r[0] || "", role: r[1] || "", salary: Number(r[2]) || 0,
    status: r[3] || "Active", salaryCycle: r[4] || "Monthly", email: r[5] || "",
    phone: r[6] || "", joinedDate: r[7] || "",
  }));

  const cryptoHoldings = cryptoData.slice(1).map((r, i) => ({
    id: "ch_r_" + i, symbol: r[0] || "", name: r[1] || "", amount: Number(r[2]) || 0,
    priceAtReceipt: Number(r[3]) || 0, source: r[4] || "", dateReceived: r[5] || "",
  }));

  const loanRequests = loanData.slice(1).map((r, i) => ({
    id: "ln_r_" + i, employee: r[0] || "", amount: Number(r[1]) || 0, emi: Number(r[2]) || 0,
    reason: r[3] || "", status: r[4] || "Pending", createdAt: r[5] || "",
  }));

  const payments = payData.slice(1).map((r, i) => ({
    id: "pay_r_" + i, employee: r[0] || "", amount: Number(r[1]) || 0, date: r[2] || "", type: r[3] || "Salary",
  }));

  return { expenses, team, cryptoHoldings, loanRequests, payments };
}
